import { useState, useCallback } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { app } from '../../../../firebase';

// Initialize RTDB
const RTDB_URL = 'https://just-rhythm-445002-s8-e92d0-513eb.firebaseio.com/';
const rtdb = getDatabase(app, RTDB_URL);

const usePickstarAccuracy = () => {
  const [loading, setLoading] = useState(false);
  const [accuracyData, setAccuracyData] = useState(null);
  const [error, setError] = useState(null);

  // Format date to ET timezone YYYY-MM-DD
  const formatDateKey = (date) => {
    const nycDate = new Date(date.toLocaleString("en-US", { timeZone: "America/New_York" }));
    const year = nycDate.getFullYear();
    const month = String(nycDate.getMonth() + 1).padStart(2, '0');
    const day = String(nycDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Calculate accuracy for a specific date
  const calculateAccuracy = useCallback(async (dateString = null) => {
    setLoading(true);
    setError(null);

    try {
      const dateKey = dateString || formatDateKey(new Date());
      console.log(`📊 Calculating PickStar accuracy for ${dateKey}`);

      // Get PickStar predictions
      const predictionsRef = ref(rtdb, `pickstar-predictions/football/${dateKey}`);
      const predictionsSnapshot = await get(predictionsRef);

      if (!predictionsSnapshot.exists()) {
        setError(`No PickStar predictions found for ${dateKey}`);
        setLoading(false);
        return null;
      }

      // Get settlement data (actual results)
      const settlementsRef = ref(rtdb, `settlements/football/${dateKey}`);
      const settlementsSnapshot = await get(settlementsRef);

      if (!settlementsSnapshot.exists()) {
        setError(`No settlement data found for ${dateKey}. Games may not be finished yet.`);
        setLoading(false);
        return null;
      }

      const predictionsData = predictionsSnapshot.val();
      const predictions = predictionsData.predictions || {};
      const settlements = settlementsSnapshot.val();

      // Match predictions with actual results
      const results = [];
      let correct = 0;
      let incorrect = 0;
      let pending = 0;
      let totalConfidence = 0;
      let correctConfidence = 0;
      let incorrectConfidence = 0;

      for (const [gameId, prediction] of Object.entries(predictions)) {
        const settlement = settlements[gameId];

        if (!settlement) {
          pending++;
          results.push({
            gameId,
            prediction,
            status: 'pending',
            result: null
          });
          continue;
        }

        // Determine actual winner from score
        const homeScore = settlement.score?.home || 0;
        const awayScore = settlement.score?.away || 0;
        const gameStatus = settlement.status?.short;

        // Skip if game not finished
        if (!['FT', 'AET', 'PEN'].includes(gameStatus)) {
          pending++;
          results.push({
            gameId,
            prediction,
            settlement,
            status: 'pending',
            result: null
          });
          continue;
        }

        let actualWinner;
        if (homeScore > awayScore) {
          actualWinner = settlement.teams.home.name;
        } else if (awayScore > homeScore) {
          actualWinner = settlement.teams.away.name;
        } else {
          actualWinner = 'Draw';
        }

        // Check if prediction was correct
        const predictedWinner = prediction.prediction?.winner;
        const confidence = prediction.prediction?.confidence || 50;
        const isCorrect = predictedWinner?.toLowerCase() === actualWinner.toLowerCase() ||
                         (actualWinner === 'Draw' && predictedWinner?.toLowerCase().includes('draw'));

        if (isCorrect) {
          correct++;
          correctConfidence += confidence;
        } else {
          incorrect++;
          incorrectConfidence += confidence;
        }
        totalConfidence += confidence;

        results.push({
          gameId,
          prediction,
          settlement,
          status: 'completed',
          result: {
            predicted: predictedWinner,
            actual: actualWinner,
            correct: isCorrect,
            confidence,
            homeScore,
            awayScore
          }
        });
      }

      const accuracy = {
        date: dateKey,
        totalGames: Object.keys(predictions).length,
        completedGames: correct + incorrect,
        pendingGames: pending,
        correct,
        incorrect,
        accuracy: correct + incorrect > 0 ? (correct / (correct + incorrect) * 100).toFixed(1) : 0,
        avgConfidence: totalConfidence / Object.keys(predictions).length,
        avgCorrectConfidence: correct > 0 ? correctConfidence / correct : 0,
        avgIncorrectConfidence: incorrect > 0 ? incorrectConfidence / incorrect : 0,
        results: results.sort((a, b) => {
          // Sort by status (completed first) then by confidence
          if (a.status !== b.status) return a.status === 'completed' ? -1 : 1;
          return (b.prediction?.prediction?.confidence || 0) - (a.prediction?.prediction?.confidence || 0);
        })
      };

      console.log(`✅ Accuracy calculated: ${accuracy.accuracy}% (${correct}/${correct + incorrect})`);
      setAccuracyData(accuracy);
      setLoading(false);
      return accuracy;

    } catch (error) {
      console.error('Error calculating accuracy:', error);
      setError(error.message);
      setLoading(false);
      return null;
    }
  }, []);

  // Get accuracy history for multiple dates
  const getAccuracyHistory = useCallback(async (days = 7) => {
    const history = [];
    const today = new Date();

    for (let i = 0; i < days; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateKey = formatDateKey(date);

      // Check if predictions exist for this date
      const predictionsRef = ref(rtdb, `pickstar-predictions/football/${dateKey}`);
      const snapshot = await get(predictionsRef);

      if (snapshot.exists()) {
        const accuracy = await calculateAccuracy(dateKey);
        if (accuracy && accuracy.completedGames > 0) {
          history.push({
            date: dateKey,
            accuracy: parseFloat(accuracy.accuracy),
            games: accuracy.completedGames,
            correct: accuracy.correct
          });
        }
      }
    }

    return history.reverse(); // Oldest to newest
  }, [calculateAccuracy]);

  return {
    loading,
    accuracyData,
    error,
    calculateAccuracy,
    getAccuracyHistory
  };
};

export default usePickstarAccuracy;